import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6s6lnja90d3mf3eh9cg';

export default function CareersApplicationForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) {
      return;
    }
    setStatus('submitting');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-[#2B2B2B] mb-10 text-center">
          Apply Now
        </h2>

        {status === 'success' ? (
          <div className="bg-[#2B2B2B] rounded-lg p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-[#C5A55A] text-5xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Application Submitted!</h3>
            <p className="text-gray-300 text-sm">
              Thank you for your interest in Deer Point Contracting. We will be in touch soon.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="careers-application-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 space-y-6"
          >
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                required
                placeholder="John Smith"
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="john@example.com"
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                placeholder="(555) 000-0000"
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent"
              />
            </div>

            {/* Position of Interest */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                Position of Interest <span className="text-red-500">*</span>
              </label>
              <select
                name="position"
                required
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent cursor-pointer"
              >
                <option value="">Select a position...</option>
                <option value="Project Manager">Project Manager</option>
                <option value="Superintendent">Superintendent</option>
                <option value="Estimator">Estimator</option>
                <option value="Project Engineer">Project Engineer</option>
                <option value="Skilled Trade / Laborer">Skilled Trade / Laborer</option>
                <option value="Administrative">Administrative</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* How Did You Hear About Us */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                How Did You Hear About Us?
              </label>
              <input
                type="text"
                name="referral_source"
                placeholder="LinkedIn, referral, job board, etc."
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent"
              />
            </div>

            {/* Message / Cover Letter */}
            <div>
              <label className="block text-sm font-semibold text-[#2B2B2B] mb-1">
                Message or Cover Letter
              </label>
              <textarea
                name="message"
                rows={6}
                maxLength={500}
                placeholder="Tell us about your experience and why you want to join Deer Point Contracting..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A55A] focus:border-transparent resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                {charCount}/500 characters
              </p>
            </div>

            {status === 'error' && (
              <p className="text-red-500 text-sm text-center">
                Something went wrong. Please try again or email us directly at{' '}
                <a href="mailto:careers@deerpointcontracting.com" className="underline">
                  careers@deerpointcontracting.com
                </a>.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full bg-[#C5A55A] hover:bg-[#b8943f] text-white font-semibold py-3 rounded-md text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {status === 'submitting' ? 'Submitting...' : 'Submit Application'}
            </button>

            <p className="text-center text-xs text-gray-400 pt-2">
              Deer Point Contracting is an equal opportunity employer.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
